// src/components/map/hooks/adapters/useMapTileSourceManager.ts
//
// Adapter hook: Tile Source Management
//
// Owns:
//   - tileUri state + tileUriRef
//   - hasTileUriChanged() (compares current and next tile URIs)
//   - updateWellAndFacilityTileUri() (calls .setTiles() on sources)
//   - filterToMapToEntities() (sets filters, paint properties, feature states)
//   - filterWellOnMap() (orchestrates tile URI changes + entity filtering)
//   - canFilterWellsOnMap() (zoom + well count guard)
//   - updateWellsAndFacilitiesOnMap() (full tile URI update + filter clearing)
//   - updateMap() effect (txnId → fetchEntitiesOnMap → filtering pipeline)
//
// Mapbox methods called: setTiles, setFilter, setPaintProperty, setFeatureState,
//                        getSource, getLayer, getZoom
//
// Store reads: selectTxnId
// API calls: fetchEntitiesOnMap
//
// This hook was extracted from MapboxWrapper as part of the concern-based
// decomposition (Phase 7). This is the highest-risk extraction because
// filterToMapToEntities is a ~115-line function that sets multiple paint
// properties and filters in a single imperative batch.
import * as Sentry from "@sentry/react";
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";

import { CLEAR_MAP_ID } from "constants/app.constants";
import {
  FACILITY_LABEL_LAYER,
  FACILITY_LAYER,
  HZ_RASTER_MVT_CROSSOVER_ZOOM,
  HZ_WELL_RASTER_LAYER,
  SPIDER_FACILITY_LAYER,
  STICK_LAYER,
  WELL_LABEL_LAYER,
  WELL_LAYER,
  WELL_LAYER_HIGHLIGHTED,
  WELL_LAYER_POINT,
  WELL_LAYER_POINT_HIGHLIGHTED
} from "constants/mapLayers.constants";
import mapboxgl from "mapbox-gl";
import { Mode, PadScenario } from "store/features";
import { RootState } from "store/rootReducer";
import { selectMode } from "store/selectors/modeSelector";
import { selectDisplaySettings, selectTxnId, useMapStore } from "stores/map";
import { useDebounce } from "use-debounce";
import { arrayOrEmpty } from "utils";

import {
  ENTITIES_ON_MAP_QUERY_KEY,
  ENTITIES_ON_MAP_STALE_TIME,
  MAP_QUERY_CACHE_TIME,
  MapLegendItem,
  fetchEntitiesOnMap
} from "api/map";

import { absolutizeServiceRoot } from "components/map/utils/absolutizeServiceRoot";

import { DashboardCapabilities } from "../../../../models/dashboard";
import { EntityKind } from "../../../../models/entityKind";
import { EntitiesInGroupModel } from "../../contexts/MapContextState";
import { setLabelVisibility } from "../../hooks/useLabelDensity";
import { setHollowDotColors } from "../../utils/baseWellGlyphs";
import { emptyFeatureCollection } from "../../utils/emptyFeatureCollection";
import { canFetchEntitiesOnMapAtZoom } from "../../utils/entitiesOnMapFetchPolicy";
import {
  TileUris,
  appendRasterThickness,
  computeHzTileUri,
  getTileUris,
  stripRasterThickness
} from "../../utils/mapboxHelper";
import { removeSpiderLayers } from "../../utils/spiderify";
import { useMapDispatch } from "../useMapDispatch";

// ── Constants ────────────────────────────────────────────────────────────────

const mapServiceEndpoint = absolutizeServiceRoot(process.env.REACT_APP_MAP_SERVICE);

// ── Types ────────────────────────────────────────────────────────────────────

/**
 * How long to wait after the last well-thickness change before rewriting the
 * raster tile URI (EVA-231). The setting is driven by a number stepper, so the
 * unthrottled signal is one change per keystroke — each of which would clear the
 * raster tile cache and re-fetch every visible tile.
 */
const RASTER_THICKNESS_DEBOUNCE_MS = 300;

/**
 * The live well-thickness settings, read from the store at call time (EVA-231).
 *
 * Read through `getState()` rather than a ref written during render (PR #6625
 * review). The three tile-URI builders that need this have empty deps and are
 * invoked through refs, so closing over a render value would pin them to
 * whatever the thickness was on first render — the same hazard
 * `updateWellAndFacilityTileUri` already solves this way for the Wells zoom
 * range. All three run after commit, so this reads exactly what a ref would
 * have held, without a mutable-during-render surface for concurrent rendering
 * to trip over.
 */
function currentWellThickness(): { line: Maybe<number>; dot: Maybe<number> } {
  const { displaySettings } = useMapStore.getState();
  return {
    line: displaySettings?.wellLineThickness,
    dot: displaySettings?.wellDotThickness
  };
}

/** setTiles exists at runtime on VectorTileSource but is missing from @types/mapbox-gl. */
interface VectorSourceWithSetTiles {
  setTiles(tiles: readonly string[]): void;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

// ZoomSlider's generic defaults are (0, 22) (see ZoomSlider.tsx
// DEFAULT_MIN_ZOOM/DEFAULT_MAX_ZOOM) — using those here would silently narrow
// today's unconfigured well maxzoom from 24 to 22. This mirrors the
// long-standing implicit default of the raster/MVT crossover below instead:
// raster (0, 8), MVT (8, 24).
const DEFAULT_WELL_LAYER_ZOOM_SETTINGS = { minzoom: 0, maxzoom: 24 };

const WELL_MVT_LAYERS = [
  WELL_LAYER,
  STICK_LAYER,
  WELL_LAYER_POINT,
  WELL_LAYER_HIGHLIGHTED,
  WELL_LAYER_POINT_HIGHLIGHTED
];

function setWellMvtZoomRange(mapbox: mapboxgl.Map, minzoom: number, maxzoom: number) {
  for (const id of WELL_MVT_LAYERS) {
    if (mapbox.getLayer(id)) {
      mapbox.setLayerZoomRange(id, minzoom, maxzoom);
    }
  }
}

/**
 * Single writer of the interactive well layers' zoom range (WELL_MVT_LAYERS
 * and the HZ_WELL_RASTER_LAYER fallback).
 * Splits the user's single zoom range across HZ_RASTER_MVT_CROSSOVER_ZOOM the
 * same way MapboxWrapper's base-well effect splits BASE_WELL_LAYER's range —
 * clamped so an out-of-span user range collapses to a zero-width range
 * (never rendered) instead of handing mapbox an inverted min > max range.
 *
 * useManageMapboxLayerStyles exempts these layers (ZOOM_RANGE_EXEMPT_LAYERS)
 * so this is the only writer — a second one racing after a style reload is
 * how EVA-237 happened (see that file's comment for the mechanism).
 */
function applyWellLayerZoomRanges(
  mapbox: mapboxgl.Map,
  zoomSettings: { minzoom: number; maxzoom: number },
  hasRaster: boolean
): void {
  if (hasRaster) {
    const mvtMinzoom = Math.min(
      Math.max(zoomSettings.minzoom, HZ_RASTER_MVT_CROSSOVER_ZOOM),
      zoomSettings.maxzoom
    );
    const rasterMaxzoom = Math.max(
      Math.min(zoomSettings.maxzoom, HZ_RASTER_MVT_CROSSOVER_ZOOM),
      zoomSettings.minzoom
    );
    if (mapbox.getLayer(HZ_WELL_RASTER_LAYER)) {
      mapbox.setLayerZoomRange(HZ_WELL_RASTER_LAYER, zoomSettings.minzoom, rasterMaxzoom);
    }
    // All well MVT layers only render at zoom >= mvtMinzoom. STICK_LAYER and
    // the highlight layers share the well sources, so leaving any ungated would
    // keep its source fetching below the raster crossover.
    // Accepted trade-off: queryRenderedFeatures returns nothing for a layer
    // outside its zoom range, so below the crossover sticks are raster-drawn
    // only — not clickable, hoverable, or lasso-selectable. Wells fall back to
    // the filter-service /contains path; sticks have no such fallback.
    setWellMvtZoomRange(mapbox, mvtMinzoom, zoomSettings.maxzoom);
  } else {
    setWellMvtZoomRange(mapbox, zoomSettings.minzoom, zoomSettings.maxzoom);
  }
}

export interface UseMapTileSourceManagerParams {
  /** Ref to the Mapbox GL map instance. */
  readonly mapboxRef: MutableRefObject<Maybe<mapboxgl.Map>>;
  /** Ref tracking whether the component is still mounted. */
  readonly isMountedRef: MutableRefObject<boolean>;
  /** Map context dispatch function. */
  readonly mapDispatch: ReturnType<typeof useMapDispatch>;
  /** Ref to the current txnId (kept in sync by shell). */
  readonly txnIdRef: MutableRefObject<ReturnType<typeof selectTxnId>>;
  /** Currently active entity kinds from Redux. */
  readonly activeEntityKinds: readonly EntityKind[];
  /** Whether the map has been fully initialized (from MapContext). */
  readonly isMapInitialized: boolean;
  /** Label update function from useLabelDensity. */
  readonly updateLabels: (id: string, layer: string) => void;
  /** Dashboard capabilities. */
  readonly capabilities: DashboardCapabilities;
}

export interface UseMapTileSourceManagerReturn {
  /** Filter wells on map using entity-level filtering (zoom permitting). */
  readonly filterWellOnMap: (
    txn: { id: string; facilityId: string },
    groupEntities: Record<string, EntitiesInGroupModel>,
    facilityEntities: Record<string, EntitiesInGroupModel>,
    scenarios: PadScenario[]
  ) => void;
  /** Update tile URIs for wells and facilities on the map. */
  readonly updateWellsAndFacilitiesOnMap: (
    wellId: string,
    facilityId: string,
    scenarios: PadScenario[],
    facilityEntities?: Record<string, EntitiesInGroupModel>
  ) => void;
  /** Check whether the map zoom and well count allow on-map filtering. */
  readonly canFilterWellsOnMap: (mapbox: mapboxgl.Map) => boolean;
  /** Current tile URIs. */
  readonly tileUri: TileUris;
  /** Ref to current tile URIs (synchronously updated). */
  readonly tileUriRef: MutableRefObject<TileUris>;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useMapTileSourceManager({
  mapboxRef,
  isMountedRef,
  mapDispatch,
  txnIdRef,
  activeEntityKinds,
  isMapInitialized,
  updateLabels,
  capabilities
}: UseMapTileSourceManagerParams): UseMapTileSourceManagerReturn {
  // ── React Query client ─────────────────────────────────────────────────
  const queryClient = useQueryClient();

  // ── Zustand selectors ──────────────────────────────────────────────────
  const txnId = useMapStore(selectTxnId);
  const displaySettings = useMapStore(selectDisplaySettings);

  // ── Redux selectors ────────────────────────────────────────────────────
  const orgId = useSelector((state: RootState) => state.auth.user.organization?.id);
  const checkedPadScenarios: PadScenario[] = useSelector(
    (state: RootState) => state.app.checkedPadScenarios
  );
  const mode = useSelector(selectMode);

  // ── Local state ────────────────────────────────────────────────────────
  const [tileUri, setTileUri] = useState<TileUris>({
    hz: "",
    v: "",
    facility: "",
    hzRaster: ""
  });
  const tileUriRef = useRef(tileUri);

  // ── hasTileUriChanged ──────────────────────────────────────────────────

  const hasTileUriChanged = useCallback(
    (nextUri: TileUris) =>
      tileUriRef.current.hz !== nextUri.hz ||
      tileUriRef.current.v !== nextUri.v ||
      tileUriRef.current.facility !== nextUri.facility ||
      tileUriRef.current.hzRaster !== nextUri.hzRaster,
    []
  );

  // ── updateWellAndFacilityTileUri ───────────────────────────────────────

  const updateWellAndFacilityTileUri = useCallback(
    (mapbox: mapboxgl.Map, uri: TileUris) => {
      // Compare each source URI individually against the previous value.
      // Only call setTiles() on sources that actually changed — setTiles()
      // clears the tile cache and forces a re-fetch, which causes a visible
      // flash if called unnecessarily (e.g. when only hzRaster changed).
      const prev = tileUriRef.current;

      if (mapbox.getSource(WELL_LAYER)) {
        if (prev.hz !== uri.hz) {
          (mapbox.getSource(WELL_LAYER) as unknown as VectorSourceWithSetTiles).setTiles([
            uri.hz
          ]);
        }
        if (prev.v !== uri.v) {
          (
            mapbox.getSource(WELL_LAYER_POINT) as unknown as VectorSourceWithSetTiles
          ).setTiles([uri.v]);
        }
      }
      if (mapbox.getSource(FACILITY_LAYER) && prev.facility !== uri.facility) {
        (
          mapbox.getSource(FACILITY_LAYER) as unknown as VectorSourceWithSetTiles
        ).setTiles([uri.facility]);
      }

      // ── Hz raster layer management ───────────────────────────────────
      // When hzRaster URL is set, ensure the raster source+layer exist and
      // restrict the well MVT layers to higher zoom levels only.
      // When hzRaster is empty (e.g. clearing), tear down the raster layer
      // and restore the MVT layer to full zoom range.
      //
      // The zoom range itself is resolved from the store here (not a
      // closed-over render value) because this callback has empty deps and
      // is invoked through refs — a stale closure would apply whatever the
      // user's range was on first render, forever.
      const rawZoomSettings =
        useMapStore.getState().displaySettings.layerZoomSettings?.[WELL_LAYER];
      const zoomSettings = {
        minzoom: rawZoomSettings?.minzoom ?? DEFAULT_WELL_LAYER_ZOOM_SETTINGS.minzoom,
        maxzoom: rawZoomSettings?.maxzoom ?? DEFAULT_WELL_LAYER_ZOOM_SETTINGS.maxzoom
      };

      if (uri.hzRaster) {
        // Computed up front because it's also needed for the raster layer's
        // initial minzoom/maxzoom at creation time, below.
        const rasterMaxzoom = Math.max(
          Math.min(zoomSettings.maxzoom, HZ_RASTER_MVT_CROSSOVER_ZOOM),
          zoomSettings.minzoom
        );
        if (mapbox.getSource(HZ_WELL_RASTER_LAYER)) {
          if (prev.hzRaster !== uri.hzRaster) {
            (
              mapbox.getSource(
                HZ_WELL_RASTER_LAYER
              ) as unknown as VectorSourceWithSetTiles
            ).setTiles([uri.hzRaster]);
          }
        } else {
          mapbox.addSource(HZ_WELL_RASTER_LAYER, {
            type: "raster",
            tiles: [uri.hzRaster],
            tileSize: 256
          });
          // This layer is outside the zustand layer store, so
          // useManageMapboxLayerStyles never re-runs to reconcile it after a
          // style reload adds it back. Set its initial visibility explicitly
          // from the persisted Wells checkbox state instead of relying on
          // that reconciliation (mapbox defaults an undeclared layout to
          // "visible", which re-shows wells the user turned off).
          const wellLayer = useMapStore
            .getState()
            .layers.find((l) => l.id === WELL_LAYER);
          mapbox.addLayer(
            {
              id: HZ_WELL_RASTER_LAYER,
              type: "raster",
              source: HZ_WELL_RASTER_LAYER,
              minzoom: zoomSettings.minzoom,
              maxzoom: rasterMaxzoom,
              layout: {
                visibility: wellLayer?.isVisible === false ? "none" : "visible"
              },
              paint: {}
            },
            mapbox.getLayer(WELL_LAYER) ? WELL_LAYER : undefined // insert just below the MVT well layer, if present
          );
        }
        applyWellLayerZoomRanges(mapbox, zoomSettings, true);
      } else {
        // Remove raster layer when no longer needed
        if (mapbox.getLayer(HZ_WELL_RASTER_LAYER)) {
          mapbox.removeLayer(HZ_WELL_RASTER_LAYER);
        }
        if (mapbox.getSource(HZ_WELL_RASTER_LAYER)) {
          mapbox.removeSource(HZ_WELL_RASTER_LAYER);
        }
        applyWellLayerZoomRanges(mapbox, zoomSettings, false);
      }
    },
    []
  );

  // ── filterToMapToEntities ──────────────────────────────────────────────

  const filterToMapToEntities = useCallback(
    (
      groupEntities: Record<string, EntitiesInGroupModel>,
      facilityEntities: Record<string, EntitiesInGroupModel>,
      hasFacility: boolean,
      mapbox: mapboxgl.Map
    ) => {
      if (!mapbox?.getLayer(WELL_LAYER)) {
        return;
      }
      const ids: Record<string, string> = {};
      const facilityIds: Record<string, string> = {};
      // Combine all the ids in each group
      for (const title of Object.keys(groupEntities)) {
        const group = groupEntities[title];
        for (const id of Object.keys(group.uwis)) {
          ids[id] = group.uwis[id];
        }
      }

      if (hasFacility) {
        removeSpiderLayers(mapbox);
        for (const title of Object.keys(facilityEntities)) {
          const group = facilityEntities[title];
          for (const id of Object.keys(group.uwis)) {
            facilityIds[id] = group.uwis[id];
          }
        }
      }
      // FACILITY_LAYER is omitted without "Base Midstream" (see getMapLayers).
      const hasFacilityLayer = !!mapbox.getLayer(FACILITY_LAYER);
      if (hasFacilityLayer) {
        mapbox.setFilter(FACILITY_LAYER, [
          "has",
          ["get", "Uwi"],
          ["literal", facilityIds]
        ]);
      }
      mapbox.setFilter(WELL_LAYER, [
        "all",
        ["has", ["get", "Uwi"], ["literal", ids]],
        ["!=", ["get", "group"], "stick"]
      ]);
      // Sticks live on their own layer but share this filtered id set, so they
      // must be restricted to the same wells — otherwise every scenario stick
      // stays visible regardless of the active filter (EVA-6899 regression).
      mapbox.setFilter(STICK_LAYER, [
        "all",
        ["has", ["get", "Uwi"], ["literal", ids]],
        ["==", ["get", "group"], "stick"]
      ]);
      mapbox.setFilter(WELL_LAYER_POINT, [
        "all",
        ["has", ["get", "Uwi"], ["literal", ids]],
        ["!=", ["get", "group"], "leg"]
      ]);
      if (!isMapInitialized) {
        mapbox.setPaintProperty(WELL_LAYER, "line-color", [
          "get",
          ["get", "Uwi"],
          ["literal", ids]
        ]);
        setHollowDotColors(mapbox, WELL_LAYER_POINT, [
          "get",
          ["get", "Uwi"],
          ["literal", ids]
        ]);
        if (hasFacilityLayer) {
          mapbox.setPaintProperty(FACILITY_LAYER, "icon-color", [
            "get",
            ["get", "Uwi"],
            ["literal", facilityIds]
          ]);
        }

        // Also update spider layer if it exists
        if (mapbox.getLayer(SPIDER_FACILITY_LAYER)) {
          mapbox.setPaintProperty(SPIDER_FACILITY_LAYER, "icon-color", [
            "get",
            ["get", "Uwi"],
            ["literal", facilityIds]
          ]);
        }
      } else {
        for (const id in ids) {
          const wellState = {
            color: ids[id]
          };
          mapbox.setFeatureState(
            {
              id,
              source: WELL_LAYER,
              sourceLayer: "eva-wells"
            },
            wellState
          );
          mapbox.setFeatureState(
            {
              id,
              source: WELL_LAYER_POINT,
              sourceLayer: "eva-wells"
            },
            wellState
          );
        }
        if (hasFacilityLayer) {
          mapbox.setPaintProperty(FACILITY_LAYER, "icon-color", [
            "case",
            ["has", "color"],
            ["get", "color"],
            ["get", ["get", "Uwi"], ["literal", facilityIds]]
          ]);
        }

        // Also update spider layer if it exists
        if (mapbox.getLayer(SPIDER_FACILITY_LAYER)) {
          mapbox.setPaintProperty(SPIDER_FACILITY_LAYER, "icon-color", [
            "get",
            ["get", "Uwi"],
            ["literal", facilityIds]
          ]);
        }
        mapbox.setPaintProperty(WELL_LAYER, "line-color", [
          "case",
          ["has", "color"],
          ["get", "color"],
          ["feature-state", "color"]
        ]);
        setHollowDotColors(mapbox, WELL_LAYER_POINT, [
          "case",
          ["has", "color"],
          ["get", "color"],
          ["feature-state", "color"]
        ]);
      }
    },
    [isMapInitialized]
  );

  // ── filterWellOnMap ────────────────────────────────────────────────────

  const filterWellOnMap = useCallback(
    (
      txn: { id: string; facilityId: string },
      groupEntities: Record<string, EntitiesInGroupModel>,
      facilityEntities: Record<string, EntitiesInGroupModel>,
      scenarios: PadScenario[]
    ) => {
      const mapbox = mapboxRef.current;
      if ((!txn?.id && !txn?.facilityId) || !mapbox) {
        filterToMapToEntities(
          groupEntities,
          facilityEntities,
          false,
          mapbox as mapboxgl.Map
        );
        return;
      }

      const uris = getTileUris(mapServiceEndpoint, orgId, scenarios);
      // Keep the raster layer in sync even when the MVT filter path is active.
      // The raster tiles must have valid data so that zooming out shows the
      // pre-rendered image immediately rather than blank tiles.
      //
      // Note this appends scenarioId with no mode check, unlike
      // updateWellsAndFacilitiesOnMap, which must omit it in group mode
      // (CONTEXT.md: "Sticks also do not render in group mode"). That is safe
      // only because filterWellOnMap is unreachable in group mode:
      // `canFilterWellsOnMap` returns false for Mode.GroupEntity, so the
      // updateMap effect routes to updateWellsAndFacilitiesOnMap instead.
      // That routing guarantee is pinned by the tests in
      // "updateMap effect — group-mode routing invariant"
      // (__tests__/useMapTileSourceManager.test.ts) — if it ever changes, this
      // site needs its own mode check.
      uris.hzRaster =
        txn.id && txn.id !== CLEAR_MAP_ID
          ? appendRasterThickness(
              computeHzTileUri(
                `${mapServiceEndpoint}/well/hz/raster/${txn.id}/{z}/{x}/{y}`,
                scenarios
              ),
              currentWellThickness().line,
              currentWellThickness().dot
            )
          : "";

      if (hasTileUriChanged(uris)) {
        updateWellAndFacilityTileUri(mapbox, uris);
        tileUriRef.current = uris;
        setTileUri(uris);
      }

      const hasFacility =
        txn.facilityId !== "" && activeEntityKinds.includes(EntityKind.Facility);

      filterToMapToEntities(groupEntities, facilityEntities, hasFacility, mapbox);
    },
    [
      mapboxRef,
      activeEntityKinds,
      orgId,
      filterToMapToEntities,
      hasTileUriChanged,
      updateWellAndFacilityTileUri
    ]
  );

  // ── canFilterWellsOnMap ────────────────────────────────────────────────

  const canFilterWellsOnMap = useCallback(
    (mapbox: mapboxgl.Map): boolean => {
      if (mode.type === Mode.GroupEntity) {
        return false;
      }
      return canFetchEntitiesOnMapAtZoom({
        zoom: mapbox.getZoom(),
        legendItems: arrayOrEmpty<MapLegendItem>(txnIdRef.current?.legend?.legendItems),
        capabilities
      });
    },
    [txnIdRef, capabilities, mode]
  );

  // ── updateWellsAndFacilitiesOnMap ──────────────────────────────────────

  const updateWellsAndFacilitiesOnMap = useCallback(
    (
      wellId: string,
      facilityId: string,
      scenarios: PadScenario[],
      facilityEntities: Record<string, EntitiesInGroupModel> = {}
    ) => {
      try {
        if (!mapboxRef?.current || !mapboxRef.current.getLayer(WELL_LAYER)) {
          return;
        }

        const facilityActive = activeEntityKinds.includes(EntityKind.Facility);

        const hasValidWellId = wellId && wellId !== CLEAR_MAP_ID;
        const hasValidFacilityId =
          facilityActive && facilityId && facilityId !== CLEAR_MAP_ID;

        const isClearing = wellId === CLEAR_MAP_ID || facilityId === CLEAR_MAP_ID;

        if (!hasValidWellId && !hasValidFacilityId && !isClearing) {
          return;
        }

        const hzBaseTileUri = `${mapServiceEndpoint}/well/{z}/{x}/{y}.hz.mvt?txn_id=${
          hasValidWellId ? wellId : CLEAR_MAP_ID
        }`;

        // Build the facility colour/id map from the fetched facility entities.
        const facilityIds: Record<string, string> = {};
        for (const title of Object.keys(facilityEntities)) {
          const group = facilityEntities[title];
          for (const id of Object.keys(group.uwis)) {
            facilityIds[id] = group.uwis[id];
          }
        }

        // Pre-rendered raster tiles for zoomed-out display.
        // At low zoom levels the raster layer replaces the MVT layer to avoid
        // the browser having to render millions of vector features.
        const hzRasterBaseUri = hasValidWellId
          ? `${mapServiceEndpoint}/well/hz/raster/${wellId}/{z}/{x}/{y}`
          : "";

        // Single-entity mode: append scenarioId so the raster's sticks match
        // the active scenario filter, same as the MVT hz URI. No hasValidWellId
        // re-check is needed — computeHzTileUri returns an empty input URI
        // unchanged, preserving the falsy raster teardown sentinel.
        const hzRasterUriSingleEntity = appendRasterThickness(
          computeHzTileUri(hzRasterBaseUri, scenarios),
          currentWellThickness().line,
          currentWellThickness().dot
        );

        // EVA-6783: the grouped facility tile (?txn_id=...) omits `facilityType`,
        // so icons fall back to a square. Always use the per-facility tile keyed
        // on orgId (matching the filterWellOnMap path) so each facility keeps its
        // `facilityType` and proper icon, coloured by the focus colour map — which
        // already carries group colours when facilities are grouped (e.g. both
        // upstream and midstream in group mode). Fall back to the grouped tile only
        // when entities haven't loaded yet or facilities are inactive, to avoid
        // showing the whole org's facilities unfiltered.
        const usePerFacilityTile = facilityActive && Object.keys(facilityIds).length > 0;
        const facilityTileUri = usePerFacilityTile
          ? `${mapServiceEndpoint}/well/facility/${orgId}/{z}/{x}/{y}`
          : `${mapServiceEndpoint}/well/facility/{z}/{x}/{y}?txn_id=${
              hasValidFacilityId ? facilityId : CLEAR_MAP_ID
            }`;

        // In group mode, use the grouped MVT endpoint for hz tiles. Vertical
        // wells are not rendered (CLEAR_MAP_ID) because group mode aggregates
        // wells into cluster features. This hz URI has never carried
        // scenarioId, and hzRaster below now deliberately doesn't either:
        // group mode has no stick identifiers to filter on, so requesting
        // scenario sticks from either endpoint would be a wasted round trip
        // the service can't act on (see CONTEXT.md "Sticks also do not
        // render in group mode").
        const uri: TileUris =
          mode.type === Mode.GroupEntity
            ? {
                hz: `${mapServiceEndpoint}/well/mvt/hz/{z}/{x}/{y}?txnId=${
                  hasValidWellId ? wellId : CLEAR_MAP_ID
                }`,
                v: `${mapServiceEndpoint}/well/{z}/{x}/{y}.vert.mvt?txn_id=${CLEAR_MAP_ID}`,
                facility: facilityTileUri,
                // Group mode: use the bare base URI — do NOT append scenarioId.
                // The group-mode payload carries no stick identifiers (see
                // CONTEXT.md "Sticks also do not render in group mode"), so the
                // service has nothing to intersect against — requesting sticks
                // here would only cost a wasted REST call, a Mongo $in, and
                // full-tile MVT encoding that gets discarded server-side.
                hzRaster: appendRasterThickness(
                  hzRasterBaseUri,
                  currentWellThickness().line,
                  currentWellThickness().dot
                )
              }
            : {
                hz: computeHzTileUri(hzBaseTileUri, scenarios),
                v: `${mapServiceEndpoint}/well/{z}/{x}/{y}.vert.mvt?txn_id=${
                  hasValidWellId ? wellId : CLEAR_MAP_ID
                }`,
                facility: facilityTileUri,
                hzRaster: hzRasterUriSingleEntity
              };

        const mapbox = mapboxRef.current;
        const didUriChange = hasTileUriChanged(uri);

        if (didUriChange) {
          updateWellAndFacilityTileUri(mapbox, uri);
        }

        mapbox.setFilter(WELL_LAYER, ["!=", ["get", "group"], "stick"]);
        mapbox.setFilter(WELL_LAYER_POINT, null);
        // Reset the stick layer back to "all sticks in this txn". Without this,
        // an id-restricted filter left over from the zoomed-in filtered path
        // (filterToMapToEntities) would linger and hide sticks here.
        mapbox.setFilter(STICK_LAYER, ["==", ["get", "group"], "stick"]);

        // FACILITY_LAYER is omitted without "Base Midstream" (see getMapLayers).
        const hasFacilityLayer = !!mapbox.getLayer(FACILITY_LAYER);
        if (hasFacilityLayer && usePerFacilityTile) {
          // The per-facility tile carries no embedded group colour, so restrict
          // it to the filtered facilities and colour them by their focus colour
          // map (mirrors the non-group facility path in filterToMapToEntities).
          const facilityFilter = ["has", ["get", "Uwi"], ["literal", facilityIds]];
          const facilityIconColor = [
            "case",
            ["has", "color"],
            ["get", "color"],
            ["get", ["get", "Uwi"], ["literal", facilityIds]]
          ];
          mapbox.setFilter(FACILITY_LAYER, facilityFilter);
          mapbox.setPaintProperty(FACILITY_LAYER, "icon-color", facilityIconColor);
          if (mapbox.getLayer(SPIDER_FACILITY_LAYER)) {
            mapbox.setFilter(SPIDER_FACILITY_LAYER, facilityFilter);
            mapbox.setPaintProperty(
              SPIDER_FACILITY_LAYER,
              "icon-color",
              facilityIconColor
            );
          }
        } else if (hasFacilityLayer) {
          // Reset icon-color so a stale per-facility focus-colour expression
          // doesn't linger on the grouped tile (whose features carry `color`).
          const defaultIconColor = [
            "case",
            ["has", "color"],
            ["get", "color"],
            ["feature-state", "color"]
          ];
          mapbox.setFilter(FACILITY_LAYER, null);
          mapbox.setPaintProperty(FACILITY_LAYER, "icon-color", defaultIconColor);
          if (mapbox.getLayer(SPIDER_FACILITY_LAYER)) {
            mapbox.setFilter(SPIDER_FACILITY_LAYER, null);
            mapbox.setPaintProperty(
              SPIDER_FACILITY_LAYER,
              "icon-color",
              defaultIconColor
            );
          }
        }
        if (didUriChange) {
          tileUriRef.current = uri;
          setTileUri(uri);
        }
      } catch (error) {
        Sentry.withScope((scope) => {
          scope.setContext("wells_facilities_update", {
            wellId,
            facilityId,
            action: "updateWellsAndFacilitiesOnMap",
            status: "failed"
          });
          scope.setLevel("error");
          Sentry.captureException(error);
        });
      }
    },
    [
      mapboxRef,
      activeEntityKinds,
      mode,
      orgId,
      hasTileUriChanged,
      updateWellAndFacilityTileUri
    ]
  );

  // ── tileUri ref sync ───────────────────────────────────────────────────

  useEffect(() => {
    if (!mapboxRef?.current || !isMapInitialized) {
      return;
    }
    tileUriRef.current = tileUri;
  }, [tileUri, isMapInitialized, mapboxRef]);

  // ── Wells zoom-slider re-apply ──────────────────────────────────────────
  // applyWellLayerZoomRanges above only re-runs from updateWellAndFacilityTileUri,
  // which fires on tile-URI changes — not on a bare zoom-slider drag. Without
  // this effect, moving the Wells slider does nothing until the next unrelated
  // tile-URI update. hasRaster is read live off the map (not tileUri state) so
  // this also re-applies correctly right after a style reload recreates the
  // raster layer.
  useEffect(() => {
    const mapbox = mapboxRef.current;
    if (!mapbox || !isMapInitialized) {
      return;
    }
    const rawZoomSettings = displaySettings.layerZoomSettings?.[WELL_LAYER];
    const zoomSettings = {
      minzoom: rawZoomSettings?.minzoom ?? DEFAULT_WELL_LAYER_ZOOM_SETTINGS.minzoom,
      maxzoom: rawZoomSettings?.maxzoom ?? DEFAULT_WELL_LAYER_ZOOM_SETTINGS.maxzoom
    };
    const hasRaster = Boolean(mapbox.getLayer(HZ_WELL_RASTER_LAYER));
    applyWellLayerZoomRanges(mapbox, zoomSettings, hasRaster);
  }, [displaySettings.layerZoomSettings?.[WELL_LAYER], isMapInitialized, mapboxRef]); // eslint-disable-line react-hooks/exhaustive-deps -- keyed on the one settings key that matters, not the whole displaySettings object (mirrors MapboxWrapper's BASE_WELL_LAYER effect)

  // ── raster thickness effect (EVA-231) ──────────────────────────────────

  // One setTiles() per interaction rather than one per keystroke on the stepper:
  // setTiles clears the tile cache and forces a re-fetch, which the user sees as
  // a flash.
  //
  // ONE timer over both values, not one each (PR #6625 review). Two independent
  // timers settle at different moments, so adjusting line thickness and then dot
  // thickness inside the window ran this effect twice — two cache clears and two
  // flashes for what the user experiences as a single adjustment.
  const thickness = useMemo(
    () => [displaySettings.wellLineThickness, displaySettings.wellDotThickness],
    [displaySettings.wellLineThickness, displaySettings.wellDotThickness]
  );
  const [[debouncedLineThickness, debouncedDotThickness]] = useDebounce(
    thickness,
    RASTER_THICKNESS_DEBOUNCE_MS
  );

  // Above the crossover the thickness settings reach the wells as Mapbox paint
  // properties (useManageMapboxLayerStyles). Below it those layers are switched
  // off and the wells are server-rendered pixels, so the setting can only reach
  // them through a new tile URI — which is what this effect writes.
  //
  // Deliberately NOT folded into the updateMap effect below: that one is keyed on
  // txn/mode and fetches entities (in the zoomed-in branch, a potentially very
  // large well-entity request). Nudging a stepper must not re-trigger any of it.
  useEffect(() => {
    const mapbox = mapboxRef.current;
    if (!mapbox || !isMapInitialized) {
      return;
    }

    const current = tileUriRef.current;
    // Only the cleared-map case (PR #6625 review corrected an earlier claim here
    // that this also covered "zoomed in past the crossover" — it does not).
    // hzRaster is non-empty at every zoom whenever there is a valid well id; what
    // gates the raster is the LAYER's maxzoom, set to the crossover where the
    // source is created. So this effect does rewrite the URI while the user is
    // zoomed in, which is correct: the URI has to already be right for the tiles
    // Mapbox fetches the moment they zoom back out.
    if (!current.hzRaster) {
      return;
    }

    // Freshness guard (PR #6625 review), mirroring the txnIdRef re-checks the
    // updateMap effect already makes after its awaits. Switching wells and then
    // nudging thickness inside the debounce window, while updateMap's entity
    // fetch is still in flight, would otherwise write the new thickness onto the
    // PREVIOUS well's raster URI — one extra setTiles() showing the wrong well's
    // tiles until the in-flight update overwrote them. The URI embeds the txn id
    // as its own path segment, so it names the well it belongs to.
    const liveTxnId = txnIdRef.current?.id;
    if (!liveTxnId || !current.hzRaster.includes(`/raster/${liveTxnId}/`)) {
      return;
    }

    const next: TileUris = {
      ...current,
      // Strip before appending so repeated changes don't accumulate params.
      hzRaster: appendRasterThickness(
        stripRasterThickness(current.hzRaster),
        debouncedLineThickness,
        debouncedDotThickness
      )
    };

    if (!hasTileUriChanged(next)) {
      return;
    }

    // Only the raster source's URI differs from what's live, and this compares
    // per source, so this is exactly one setTiles() on the raster.
    updateWellAndFacilityTileUri(mapbox, next);
    tileUriRef.current = next;
    setTileUri(next);
  }, [
    debouncedLineThickness,
    debouncedDotThickness,
    isMapInitialized,
    mapboxRef,
    txnIdRef,
    hasTileUriChanged,
    updateWellAndFacilityTileUri
  ]);

  // ── updateMap effect ───────────────────────────────────────────────────

  useEffect(() => {
    if (!isMapInitialized) {
      return;
    }

    async function updateMap() {
      let id = txnId.id;
      const mapbox = mapboxRef.current;
      if (!id) {
        id = CLEAR_MAP_ID;
      }
      if (!id || !mapbox) {
        return;
      }

      function clearLabels(labelLayer: string) {
        if (
          mapbox &&
          mapbox.isStyleLoaded() &&
          mapbox.getLayer(labelLayer) &&
          mapbox.getSource(labelLayer)
        ) {
          (mapbox.getSource(labelLayer) as mapboxgl.GeoJSONSource)?.setData({
            ...emptyFeatureCollection
          });
        }
      }

      const shouldFilterOnMap = canFilterWellsOnMap(mapbox);

      // Facility entities are needed for per-facility tile coloring in both branches
      // below; unlike well entities they're small, so fetch them regardless of
      // raster mode instead of only in the filtered branch.
      const facilityEntitiesPromise = queryClient.fetchQuery(
        [ENTITIES_ON_MAP_QUERY_KEY, txnId.facilityId],
        () => fetchEntitiesOnMap(txnId.facilityId),
        { staleTime: ENTITIES_ON_MAP_STALE_TIME, cacheTime: MAP_QUERY_CACHE_TIME }
      );

      // In raster mode the tile endpoints carry the visual result already.
      // Do not block source updates on fetchEntitiesOnMap() for well entities,
      // which can be very large and is not used to color or filter the
      // rasterized wells.
      if (!shouldFilterOnMap) {
        const facilityEntities = await facilityEntitiesPromise;

        // Abort if txnId changed while the facility fetch was in flight, so a
        // rapid well/scenario switch while zoomed out doesn't revert the map to
        // stale data. Mirrors the guard in the filtered branch below.
        if (
          txnIdRef.current.id !== txnId.id ||
          txnIdRef.current.facilityId !== txnId.facilityId
        ) {
          return;
        }

        updateWellsAndFacilitiesOnMap(
          id,
          txnId.facilityId,
          checkedPadScenarios,
          facilityEntities
        );

        if (isMountedRef.current) {
          mapDispatch({
            payload: {
              entitiesInGroup: {},
              facilitiesInGroup: {}
            }
          });

          setLabelVisibility(
            mapbox,
            WELL_LABEL_LAYER,
            false,
            displaySettings.wellLabelSize,
            displaySettings.labelDensity?.[WELL_LABEL_LAYER] ?? 0
          );
          setLabelVisibility(
            mapbox,
            FACILITY_LABEL_LAYER,
            false,
            displaySettings.facilityLabelSize,
            displaySettings.labelDensity?.[FACILITY_LABEL_LAYER] ?? 0
          );
        }

        clearLabels(WELL_LABEL_LAYER);
        clearLabels(FACILITY_LABEL_LAYER);
        return;
      }

      const [groupEntities, facilityEntities] = await Promise.all([
        queryClient.fetchQuery(
          [ENTITIES_ON_MAP_QUERY_KEY, id],
          () => fetchEntitiesOnMap(id),
          { staleTime: ENTITIES_ON_MAP_STALE_TIME, cacheTime: MAP_QUERY_CACHE_TIME }
        ),
        facilityEntitiesPromise
      ]);

      // Check if txnId has changed since this async operation started
      // If it has, abort this update to prevent stale data from overwriting newer updates
      if (
        txnIdRef.current.id !== txnId.id ||
        txnIdRef.current.facilityId !== txnId.facilityId
      ) {
        return;
      }

      if (id && id !== CLEAR_MAP_ID) {
        updateLabels(id, WELL_LABEL_LAYER);
      } else {
        clearLabels(WELL_LABEL_LAYER);
      }
      if (txnId.facilityId && txnId.facilityId !== "clear") {
        updateLabels(txnId.facilityId, FACILITY_LABEL_LAYER);
      } else {
        clearLabels(FACILITY_LABEL_LAYER);
      }
      const wellEntities = activeEntityKinds.includes(EntityKind.Well)
        ? groupEntities
        : {};

      if (isMountedRef.current) {
        mapDispatch({
          payload: {
            entitiesInGroup: wellEntities,
            facilitiesInGroup: facilityEntities
          }
        });
        const wellCount = Object.keys(wellEntities).length;
        const facilityCount = Object.keys(facilityEntities).length;

        setLabelVisibility(
          mapbox,
          WELL_LABEL_LAYER,
          wellCount > 0 && activeEntityKinds.includes(EntityKind.Well),
          displaySettings.wellLabelSize,
          displaySettings.labelDensity?.[WELL_LABEL_LAYER] ?? 0
        );
        setLabelVisibility(
          mapbox,
          FACILITY_LABEL_LAYER,
          facilityCount > 0 && activeEntityKinds.includes(EntityKind.Facility),
          displaySettings.facilityLabelSize,
          displaySettings.labelDensity?.[FACILITY_LABEL_LAYER] ?? 0
        );
      }
      // checkedPadScenarios is read directly from the reactive scope here (not from a ref)
      // because this code runs inside a useEffect that re-executes on every change,
      // so the value is always fresh.
      filterWellOnMap(txnId, wellEntities, facilityEntities, checkedPadScenarios);
    }

    updateMap();
  }, [txnId, isMapInitialized, activeEntityKinds, checkedPadScenarios, mode]); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally keyed on these reactive values only

  return {
    filterWellOnMap,
    updateWellsAndFacilitiesOnMap,
    canFilterWellsOnMap,
    tileUri,
    tileUriRef
  };
}
